import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Send,
  Building2,
  Briefcase,
  FileText,
  GraduationCap,
  FlaskConical,
  DollarSign,
  X,
  Share2,
  Check,
  Phone
} from "lucide-react";
import "../StudentDashboard/StudentDashboard.css";

function PositionModal({ position, isOpen, onClose }) {
  const navigate = useNavigate();
  const [shareCopied, setShareCopied] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle share functionality
  const handleShare = async (e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/position/${position._id}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  if (!isOpen || !position) return null;

  const formatLocation = () => {
    if (position.city && position.country) {
      return `${position.city}, ${position.country}`;
    }
    return position.city || position.country || "Location not specified";
  };

  const formatStartDate = () => {
    if (!position.start_semester && !position.start_year)
      return "TBD";
    if (position.start_semester && position.start_year)
      return `${position.start_semester} ${position.start_year}`;
    if (position.start_semester) return position.start_semester;
    if (position.start_year) return position.start_year;
    return "TBD";
  };

  const InfoItem = ({ label, value, dotColor = "blue", children }) => (
    <div className="std-modal-info-item">
      <div className="std-modal-info-label">
        <div className={`std-info-dot ${dotColor}`} />
        {label}
      </div>
      <div className="std-modal-info-value">
        {children || value || "N/A"}
      </div>
    </div>
  );

  const ModalContent = () => (
    <div className="std-modal-overlay" onClick={onClose}>
      <div
        className="std-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Card */}
        <div className="std-modal-header-card">
          <div className="header-main">
            <h2 className="std-modal-title">{position.title?.trim() || "Untitled Position"}</h2>
            <div className="std-modal-loc">
              <MapPin size={18} />
              {formatLocation()}
            </div>
          </div>
          <div className="header-actions" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {(position.link || position.native) ? (
              <a
                href={position.link && position.link.startsWith("http") ? position.link : `https://${position.link}`}
                target="_blank"
                rel="noopener noreferrer"
                className="std-modal-apply-btn"
                onClick={(e) => {
                  if (position.native) {
                    e.preventDefault();
                    navigate(`/apply/${position._id}`);
                  }
                  e.stopPropagation();
                }}
              >
                <Send size={18} />
                Apply Here
              </a>
            ) : (
              <button className="std-modal-apply-btn" style={{ opacity: 0.7, cursor: 'not-allowed' }}>
                No Link Available
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleShare(e);
              }}
              className="std-modal-apply-btn"
              style={{
                backgroundColor: shareCopied ? '#10b981' : '#8FBFC2',
                color: '#061941'
              }}
              aria-label="Share position"
              title="Copy shareable link"
            >
              {shareCopied ? (
                <>
                  <Check size={18} />
                  Link Copied
                </>
              ) : (
                <>
                  <Share2 size={18} />
                  Share
                </>
              )}
            </button>
          </div>
          <button
            onClick={onClose}
            className="std-modal-close-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content Grid */}
        <div className="std-modal-grid">
          {/* Institution Details */}
          <div className="std-info-card">
            <div className="std-card-header">
              <div className="std-icon-wrapper blue">
                <Building2 size={24} />
              </div>
              <h3 className="std-card-title">Position Details</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <InfoItem label="Institution" value={position.institution} dotColor="blue" />
              <div className="std-modal-info-row">
                <InfoItem label="Start Date" value={formatStartDate()} dotColor="blue" />
                <InfoItem label="Deadline" value={position.deadline} dotColor="blue" />
              </div>
              <div className="std-modal-info-item">
                <div className="std-modal-info-label">
                  <div className="std-info-dot blue"></div>
                  RESEARCH FIELDS:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingLeft: '1.5rem', marginTop: '0.25rem' }}>
                  {position.fields?.map((field, index) => (
                    <span key={index} className="std-info-tag">{field}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Financial Details */}
          <div className="std-info-card">
            <div className="std-card-header">
              <div className="std-icon-wrapper green">
                <DollarSign size={24} />
              </div>
              <h3 className="std-card-title">Financial Details</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <InfoItem label="Funding Status" value={position.financial_info?.fully_funded} dotColor="green" />
              <InfoItem label="Funding Source" value={position.financial_info?.funding_source} dotColor="green" />
              <InfoItem label="Duration" value={position.financial_info?.expected_graduation_time} dotColor="green" />
            </div>
          </div>

          {/* Lab Environment (if data exists) */}
          {(position.lab_environment?.size || position.lab_environment?.publication_rate || position.lab_environment?.equipment_resources || position.lab_environment?.collaboration) && (
            <div className="std-info-card">
              <div className="std-card-header">
                <div className="std-icon-wrapper purple">
                  <FlaskConical size={24} />
                </div>
                <h3 className="std-card-title">Lab Environment</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div className="std-modal-info-row">
                  <InfoItem label="Lab Size" value={position.lab_environment?.size} dotColor="purple" />
                  <InfoItem label="Publication Rate" value={position.lab_environment?.publication_rate} dotColor="purple" />
                </div>
                <InfoItem label="Equipment Resources" value={position.lab_environment?.equipment_resources} dotColor="purple" />
                <InfoItem label="Collaboration" value={position.lab_environment?.collaboration} dotColor="purple" />
              </div>
            </div>
          )}

          {/* Requirements (if data exists) */}
          {(position.minimum_requirements) && (
            <div className="std-info-card">
              <div className="std-card-header">
                <div className="std-icon-wrapper orange">
                  <GraduationCap size={24} />
                </div>
                <h3 className="std-card-title">Requirements</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div className="std-modal-info-row">
                  <InfoItem label="Degree Level" value={position.minimum_requirements?.degree_level} dotColor="orange" />
                  <InfoItem label="Degree Field" value={position.minimum_requirements?.degree_field} dotColor="orange" />
                </div>
                <div className="std-modal-info-row">
                  <InfoItem label="GPA" value={position.minimum_requirements?.gpa} dotColor="orange" />
                  <InfoItem label="GRE Score" value={position.minimum_requirements?.gre} dotColor="orange" />
                  <InfoItem label="TOEFL" value={position.minimum_requirements?.toefl} dotColor="orange" />
                </div>
                <InfoItem label="Research Experience" value={position.minimum_requirements?.research_experience} dotColor="orange" />
              </div>
            </div>
          )}

          {/* Role Description */}
          <div className="std-info-card full-width">
            <div className="std-card-header">
              <div className="std-icon-wrapper orange">
                <FileText size={24} />
              </div>
              <h3 className="std-card-title">Role Description</h3>
            </div>
            <div className="std-description">
              {position.description ? position.description.split("\n").map((paragraph, index) => (
                <p key={index} style={{ marginBottom: '1rem' }}>
                  {paragraph}
                </p>
              )) : <p>No description available.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(<ModalContent />, document.body);
}

export default PositionModal;
